import React, { useState } from 'react';
import { FileText, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import type { DealNarrative } from '../../types/calculations';
import { GoldBadge } from '../Luxury';

interface DealNarrativeCardProps {
  narrative: DealNarrative | null | undefined;
}

/**
 * Format relative timestamp from ISO date string
 * Examples: "2h ago", "5d ago", "Just now"
 */
const formatTimeAgo = (isoDate: string): string => {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
};

/**
 * Extract 2-3 key bullet points from the narrative text.
 * Strategy:
 * 1. First sentence of paragraph 1 (usually contains verdict + rationale)
 * 2. First sentence of paragraph 2 (usually the key quantified insight)
 * 3. First sentence of last paragraph (usually risks or forward look)
 */
function extractKeyBullets(narrativeText: string): string[] {
  const paragraphs = narrativeText
    .split('\n\n')
    .filter((p) => p.trim().length > 0);

  if (paragraphs.length === 0) return [];

  const getFirstSentence = (para: string): string => {
    // Split on sentence endings followed by space, take first
    const match = para.match(/^(.+?[.!?])\s/);
    return match ? match[1].trim() : para.slice(0, 140).trim() + '...';
  };

  const bullets: string[] = [];

  // Bullet 1: First sentence (verdict)
  bullets.push(getFirstSentence(paragraphs[0]));

  // Bullet 2: First sentence of paragraph 2 (key insight)
  if (paragraphs.length >= 2) {
    bullets.push(getFirstSentence(paragraphs[1]));
  }

  // Bullet 3: First sentence of last paragraph (if different from #2)
  if (paragraphs.length >= 3) {
    bullets.push(getFirstSentence(paragraphs[paragraphs.length - 1]));
  }

  return bullets;
}

/**
 * DealNarrativeCard
 *
 * Displays AI-generated investment thesis from Phase 91 NarrativeEngine.
 * - Key bullet points visible by default
 * - Collapsible full narrative on toggle
 * - Staleness detection warning
 * - Model attribution footer
 * - Luxury dark theme styling (obsidian/charcoal/gold)
 * - Graceful absence when narrative data missing
 */
export const DealNarrativeCard: React.FC<DealNarrativeCardProps> = ({ narrative }) => {
  const [expanded, setExpanded] = useState(false);

  // Graceful absence — no empty card, no placeholder
  // Defense-in-depth: also guard against word_count=0 fallback narratives
  if (!narrative?.narrative_text || narrative.word_count === 0) {
    return null;
  }

  // Model label: guard against null, undefined, empty string, or literal "n/a" from stale cache
  const modelLabel =
    narrative.model_used && narrative.model_used !== 'n/a'
      ? narrative.model_used
          .replace('claude-', 'Claude ')
          .replace(/-\d{8}$/, '') // strip date suffix like -20250514
      : 'Claude Sonnet';

  // Split narrative into paragraphs
  const paragraphs = narrative.narrative_text
    .split('\n\n')
    .filter((p) => p.trim().length > 0);

  const bullets = extractKeyBullets(narrative.narrative_text);
  const timeAgo = formatTimeAgo(narrative.generated_at);
  const dataHashShort = narrative.data_hash.slice(0, 8);

  return (
    <div className="rounded-xl border border-slate-dark bg-charcoal p-6">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-gold" />
          <h3 className="text-sm uppercase tracking-[0.12em] text-gold font-semibold">
            Investment Thesis
          </h3>
          <GoldBadge variant="gold">AI-Generated</GoldBadge>
        </div>
        <div className="flex items-center gap-2 text-sm text-stone-700 font-mono">
          <span>{narrative.word_count} words</span>
          <span>•</span>
          <span>{timeAgo}</span>
        </div>
      </div>

      {/* Staleness Warning */}
      {narrative.is_stale && (
        <div className="mb-4 rounded-lg bg-amber-900/20 border border-amber-600/40 p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-400 mb-1">
              Narrative May Be Outdated
            </p>
            <p className="text-sm text-amber-200/80">
              Underlying property data has changed since this narrative was generated.
            </p>
          </div>
        </div>
      )}

      {/* Key Bullet Points - Always Visible */}
      <ul className="space-y-2 mb-3">
        {bullets.map((bullet, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-ivory/80 leading-relaxed">
            <span className="text-gold mt-0.5 flex-shrink-0">&#x2022;</span>
            <span>{bullet}</span>
          </li>
        ))}
      </ul>

      {/* Expand/Collapse Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-sm text-gold hover:text-gold/80 transition-colors mb-3"
      >
        {expanded ? (
          <>
            <ChevronDown className="w-3.5 h-3.5" />
            <span>Show less</span>
          </>
        ) : (
          <>
            <ChevronRight className="w-3.5 h-3.5" />
            <span>Read full analysis ({narrative.word_count} words)</span>
          </>
        )}
      </button>

      {/* Full Narrative Paragraphs - Collapsible */}
      {expanded && (
        <div className="space-y-4 text-sm text-ivory/70 leading-relaxed border-t border-slate-dark pt-4">
          {paragraphs.map((para, idx) => (
            <p key={idx} className="break-words whitespace-normal">
              {para}
            </p>
          ))}
        </div>
      )}

      {/* Footer - Model Attribution */}
      <div className="mt-4 pt-4 border-t border-slate-dark text-sm text-stone-700 flex items-center justify-between">
        <span>Powered by {modelLabel}</span>
        <span className="font-mono text-stone-700">{dataHashShort}</span>
      </div>
    </div>
  );
};
